import { Fragment, useMemo } from 'react';
import { P2GDate } from '@parcel2go/core-ui';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ToInlineCommaList } from '../../../lib/helpers/StringHelper';
import { P2GText } from '@parcel2go/core-ui';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { GetOrderReferenceBreakdown } from '../item/OrderReferenceNumberUtils';
import { P2GChip } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { GetSkus } from '../item/GetSkus';
import { onlyUnique } from '../../../lib/helpers/ArrayHelper';
import { GetOrderLinks } from '../item/GetOrderLinks';
import { P2GLink } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';

interface props {
  item: SmartSendItemApiModel;
}

export default function ItemContentsIntro(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const intro = useMemo(
    () => ({
      skus: GetSkus(props.item),
      orderLinks: GetOrderLinks(props.item),
      accounts: GetOrderReferenceBreakdown(props.item.MarketplaceOrderLines).filter(
        (a) => a.customerReferences.length > 0
      ),
      sources: props.item.MarketplaceOrderLines
        ? props.item.MarketplaceOrderLines.map((x) => x.Source?.Name).filter(onlyUnique)
        : []
    }),
    [props.item.MarketplaceOrderLines]
  );

  if (!intro) {
    return null;
  }

  const isManualItem =
    intro.sources.length === 0 || intro.sources.every((s) => ['Grouped', 'Manual', 'CSV'].includes(s));
  const statusIsSold = props.item.MarketplaceStatus === 'Sold';

  return (
    <P2GGrid container direction="column" spacing={1}>
      <P2GGrid item>
        {isManualItem ? (
          <P2GText component="p" variant="body2">
            <Trans
              i18nKey="CONTENTS_MANUAL_ITEM_ADDED"
              t={t}
              components={{ date: <P2GDate date={new Date(props.item.Created)} variant="body2" size="medium" /> }}
              values={{ app: SettingsCache.settings.SmartSend.Name }}
              defaults="Added to {{app}} on <date />"
            />
          </P2GText>
        ) : (
          <P2GText component="p" variant="body2">
            {statusIsSold ? (
              <Trans
                i18nKey="CONTENTS_ITEM_IMPORT_SOURCE"
                t={t}
                values={{ source: ToInlineCommaList(intro.sources) }}
                defaults="Imported from {{source}} on <date />."
                components={{ date: <P2GDate date={new Date(props.item.Created)} variant="body2" size="medium" /> }}
              />
            ) : (
              <Trans
                i18nKey="CONTENTS_ITEM_BOOKED_DATE"
                t={t}
                components={{ date: <P2GDate date={new Date(props.item.Created)} variant="body2" size="medium" /> }}
                defaults="Booked on <date />."
              />
            )}
          </P2GText>
        )}
      </P2GGrid>
      {intro.accounts.length > 0 && (
        <>
          <P2GGrid item>
            <P2GText variant="body2">
              <Trans i18nKey="CONTENTS_ORDER_NUMBERS" t={t} defaults="<strong>Order Numbers</strong>" />
            </P2GText>
          </P2GGrid>
          {intro.accounts.map((a) => (
            <Fragment key={a.accountName}>
              {(a.totalAccountsForSource > 1 || intro.accounts.length > 1) && (
                <P2GGrid item>
                  <Trans
                    i18nKey="CONTENTS_ORDER_NUMBERS_SOURCE"
                    t={t}
                    components={{
                      span: <P2GText variant="body2" component="span" />,
                      chip: <P2GChip size="small" label={a.accountName} color="info" />
                    }}
                    defaults="<span>Account: <chip /><span>"
                  />
                </P2GGrid>
              )}
              <P2GGrid item>
                <P2GGrid container spacing={1} alignItems="center">
                  {a.customerReferences.map((r) => (
                    <P2GGrid item key={r}>
                      {intro.orderLinks[r] ? (
                        <P2GLink href={intro.orderLinks[r]} target="_blank">
                          <P2GChip size="small" label={r} color="primary" clickable />
                        </P2GLink>
                      ) : (
                        <P2GChip size="small" label={r} color="primary" />
                      )}
                    </P2GGrid>
                  ))}
                </P2GGrid>
              </P2GGrid>
            </Fragment>
          ))}
        </>
      )}
      {intro.skus.length > 0 && (
        <>
          <P2GGrid item>
            <P2GText variant="body2">
              <Trans i18nKey="CONTENTS_SKUS" t={t} defaults="<strong>SKUs</strong>" />
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GGrid container spacing={1} alignItems="center">
              {intro.skus.map((s) => (
                <P2GGrid item key={s}>
                  <P2GChip size="small" label={s} color="info" />
                </P2GGrid>
              ))}
            </P2GGrid>
          </P2GGrid>
        </>
      )}
    </P2GGrid>
  );
}
